import React from "react";
import { EditButton } from "./EditButton";
import countryCodes from "../data/countryCodes.json";

interface CompanyDetailsProps {
    user: any;
    isEditingCompanyName: boolean;
    isEditingCompanyTitle: boolean;
    isEditingCompanyPhoneNumber: boolean;
    isEditingCompanyStreetAddress: boolean;
    isEditingCompanyCity: boolean;
    isEditingCompanyState: boolean;
    isEditingCompanyCountry: boolean;
    isEditingCompanyPostalCode: boolean;
    companyName: string;
    companyTitle: string;
    companyPhoneNumber: string;
    companyStreetAddress: string;
    companyCity: string;
    companyState: string;
    companyCountry: string;
    companyPostalCode: string;
    onCompanyNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onCompanyTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onCompanyPhoneNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onCompanyStreetAddressChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onCompanyCityChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onCompanyStateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onCompanyCountryChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    onCompanyPostalCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onCompanyNameEdit: () => void;
    onCompanyTitleEdit: () => void;
    onCompanyPhoneNumberEdit: () => void;
    onCompanyStreetAddressEdit: () => void;
    onCompanyCityEdit: () => void;
    onCompanyStateEdit: () => void;
    onCompanyCountryEdit: () => void;
    onCompanyPostalCodeEdit: () => void;
    onSave: (field: string, value: string) => Promise<void>;
}

export default function CompanyDetails({
    user,
    isEditingCompanyName,
    isEditingCompanyTitle,
    isEditingCompanyPhoneNumber,
    isEditingCompanyStreetAddress,
    isEditingCompanyCity,
    isEditingCompanyState,
    isEditingCompanyCountry,
    isEditingCompanyPostalCode,
    companyName,
    companyTitle,
    companyPhoneNumber,
    companyStreetAddress,
    companyCity,
    companyState,
    companyCountry,
    companyPostalCode,
    onCompanyNameChange,
    onCompanyTitleChange,
    onCompanyPhoneNumberChange,
    onCompanyStreetAddressChange,
    onCompanyCityChange,
    onCompanyStateChange,
    onCompanyCountryChange,
    onCompanyPostalCodeChange,
    onCompanyNameEdit,
    onCompanyTitleEdit,
    onCompanyPhoneNumberEdit,
    onCompanyStreetAddressEdit,
    onCompanyCityEdit,
    onCompanyStateEdit,
    onCompanyCountryEdit,
    onCompanyPostalCodeEdit,
    onSave,
}: CompanyDetailsProps) {
    const countryNames = Object.keys(countryCodes).sort();

    const handleSave = async (field: string, value: string, editToggle: () => void) => {
        await onSave(field, value);
        editToggle();
    };

    return (
        <div style={{ maxWidth: "500px" }} className="mt-8 bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Company Details</h2>
            <div className="space-y-4">
                <div>
                    <label className="text-sm text-gray-500">Company Name</label>
                    <div className="relative">
                        <input
                            type="text"
                            value={companyName}
                            disabled={!isEditingCompanyName}
                            onChange={onCompanyNameChange}
                            placeholder="Enter company name"
                            className={`mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 ${!isEditingCompanyName
                                ? "bg-gray-50 dark:bg-gray-700"
                                : "bg-white dark:bg-gray-800"
                                } text-gray-900 dark:text-gray-100`}
                        />
                        <EditButton
                            isEditing={isEditingCompanyName}
                            onClick={isEditingCompanyName
                                ? () => handleSave('companyName', companyName, onCompanyNameEdit)
                                : onCompanyNameEdit
                            }
                        />
                    </div>
                </div>
                <div>
                    <label className="text-sm text-gray-500">Your Title</label>
                    <div className="relative">
                        <input
                            type="text"
                            value={companyTitle}
                            disabled={!isEditingCompanyTitle}
                            onChange={onCompanyTitleChange}
                            placeholder="Enter your job title (e.g., CEO, Manager, Director)"
                            className={`mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 ${!isEditingCompanyTitle
                                ? "bg-gray-50 dark:bg-gray-700"
                                : "bg-white dark:bg-gray-800"
                                } text-gray-900 dark:text-gray-100`}
                        />
                        <EditButton
                            isEditing={isEditingCompanyTitle}
                            onClick={isEditingCompanyTitle
                                ? () => handleSave('companyTitle', companyTitle, onCompanyTitleEdit)
                                : onCompanyTitleEdit
                            }
                        />
                    </div>
                </div>
                <div>
                    <label className="text-sm text-gray-500">Company Phone Number</label>
                    <div className="relative">
                        <input
                            type="tel"
                            value={companyPhoneNumber}
                            disabled={!isEditingCompanyPhoneNumber}
                            onChange={onCompanyPhoneNumberChange}
                            placeholder="Enter company phone number"
                            maxLength={14}
                            className={`mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 ${!isEditingCompanyPhoneNumber
                                ? "bg-gray-50 dark:bg-gray-700"
                                : "bg-white dark:bg-gray-800"
                                } text-gray-900 dark:text-gray-100`}
                        />
                        <EditButton
                            isEditing={isEditingCompanyPhoneNumber}
                            onClick={isEditingCompanyPhoneNumber
                                ? () => handleSave('companyPhoneNumber', companyPhoneNumber, onCompanyPhoneNumberEdit)
                                : onCompanyPhoneNumberEdit
                            }
                        />
                    </div>
                </div>
                <div>
                    <label className="text-sm text-gray-500">Company Street Address</label>
                    <div className="relative">
                        <input
                            type="text"
                            value={companyStreetAddress}
                            disabled={!isEditingCompanyStreetAddress}
                            onChange={onCompanyStreetAddressChange}
                            placeholder="Enter company street address"
                            className={`mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 ${!isEditingCompanyStreetAddress
                                ? "bg-gray-50 dark:bg-gray-700"
                                : "bg-white dark:bg-gray-800"
                                } text-gray-900 dark:text-gray-100`}
                        />
                        <EditButton
                            isEditing={isEditingCompanyStreetAddress}
                            onClick={isEditingCompanyStreetAddress
                                ? () => handleSave('companyStreetAddress', companyStreetAddress, onCompanyStreetAddressEdit)
                                : onCompanyStreetAddressEdit
                            }
                        />
                    </div>
                </div>
                <div>
                    <label className="text-sm text-gray-500">Company City</label>
                    <div className="relative">
                        <input
                            type="text"
                            value={companyCity}
                            disabled={!isEditingCompanyCity}
                            onChange={onCompanyCityChange}
                            placeholder="Enter company city"
                            className={`mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 ${!isEditingCompanyCity
                                ? "bg-gray-50 dark:bg-gray-700"
                                : "bg-white dark:bg-gray-800"
                                } text-gray-900 dark:text-gray-100`}
                        />
                        <EditButton
                            isEditing={isEditingCompanyCity}
                            onClick={isEditingCompanyCity
                                ? () => handleSave('companyCity', companyCity, onCompanyCityEdit)
                                : onCompanyCityEdit
                            }
                        />
                    </div>
                </div>
                <div>
                    <label className="text-sm text-gray-500">State / Province</label>
                    <div className="relative">
                        <input
                            type="text"
                            value={companyState}
                            disabled={!isEditingCompanyState}
                            onChange={onCompanyStateChange}
                            placeholder="Enter state or province"
                            maxLength={2}
                            className={`mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 ${!isEditingCompanyState
                                ? "bg-gray-50 dark:bg-gray-700"
                                : "bg-white dark:bg-gray-800"
                                } text-gray-900 dark:text-gray-100`}
                        />
                        <EditButton
                            isEditing={isEditingCompanyState}
                            onClick={isEditingCompanyState
                                ? () => handleSave('companyState', companyState, onCompanyStateEdit)
                                : onCompanyStateEdit
                            }
                        />
                    </div>
                </div>
                <div>
                    <label className="text-sm text-gray-500">Company Country</label>
                    <div className="relative">
                        {isEditingCompanyCountry ? (
                            <select
                                value={companyCountry}
                                onChange={onCompanyCountryChange}
                                className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            >
                                <option value="">Select country</option>
                                {countryNames.map((countryName) => (
                                    <option key={countryName} value={countryName}>
                                        {countryName}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <input
                                type="text"
                                value={companyCountry}
                                disabled
                                placeholder="Select country"
                                className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            />
                        )}
                        <EditButton
                            isEditing={isEditingCompanyCountry}
                            onClick={isEditingCompanyCountry
                                ? () => handleSave('companyCountry', companyCountry, onCompanyCountryEdit)
                                : onCompanyCountryEdit
                            }
                        />
                    </div>
                </div>
                <div>
                    <label className="text-sm text-gray-500">Company Postal Code</label>
                    <div className="relative">
                        <input
                            type="text"
                            value={companyPostalCode}
                            disabled={!isEditingCompanyPostalCode}
                            onChange={onCompanyPostalCodeChange}
                            placeholder="Enter company postal code"
                            maxLength={7}
                            className={`mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 ${!isEditingCompanyPostalCode
                                ? "bg-gray-50 dark:bg-gray-700"
                                : "bg-white dark:bg-gray-800"
                                } text-gray-900 dark:text-gray-100`}
                        />
                        <EditButton
                            isEditing={isEditingCompanyPostalCode}
                            onClick={isEditingCompanyPostalCode
                                ? () => handleSave('companyPostalCode', companyPostalCode, onCompanyPostalCodeEdit)
                                : onCompanyPostalCodeEdit
                            }
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
